//#region ../src/state/alignment-maturity.ts
/**
* Session GPS extent (m) at which an alignment counts as MATURE.
*
* Measured (`ar/qr/qr-anchor-mint.start-at-code.test.ts`, `start` and
* `left`; and the Tour Viewer's `visit-settle.left-behind.test.ts`; 5 m GPS
* accuracy, Gauss-Markov wander 0.25 x accuracy over 60 s plus white 0.15 x
* accuracy; SLAM drift 0.5-2 % and 0.5-2 degrees per 100 m):
* - 10-20 m is already met at a mid-walk sighting, so it is the sighting's
*   own alignment, and for a code seen only at the start it is WORSE than
*   the alignment at the walk's end (up to 8 degrees p50, 15-24 p90): the
*   reversing value.
* - 40 m (the owner's choice, D34, 2026-10-04) keeps a code left behind at
*   1.1-1.7 m; its p90 heading is 5-6 degrees at 2 % translation drift.
* - 80 m (the session's earlier recommendation, shipped from D28 revised
*   until D34) gives the same positions and a p90 heading of 3-4 degrees
*   there; notes left behind 1.0-1.2 m p50. It loses to 40 m when 80 m of
*   extent comes long after the object (a code measured at the start of
*   long first walks: 5.3 m against 1.6 m at 2 % / 2 degrees; a 100 m
*   out-and-back authoring visit: 1.7 / 3.8 m against 1.1 / 1.7 m).
* The owner chose 40 m on this simulation's evidence, trading 1-2 degrees of
* p90 heading at high drift for maturity on short walks; the sweep on real
* recordings is filed as the check on it. A lower GPS accuracy figure
* shrinks the noise share of the extent, so the floor would mean a longer
* real walk at 2-3 m accuracy.
*/
const MATURE_GPS_EXTENT_M = 40;
/** An alignment an object can be placed through: a matrix and a zero. */
function isUsableAlignment(a) {
	return a.alignmentMatrix !== null && a.zero !== null;
}
/**
* The floor (m) to use: {@link MATURE_GPS_EXTENT_M} when absent.
*
* @throws RangeError when it is not a positive, finite number of metres.
*/
function checkMatureGpsExtentM(floorM) {
	const floor = floorM ?? 40;
	if (!Number.isFinite(floor) || floor <= 0) throw new RangeError(`matureGpsExtentM must be a positive, finite number of metres; got ${String(floor)}`);
	return floor;
}
/**
* Usable, and its GPS extent reaches `floorM`. An unknown or non-finite
* extent is never mature: the floor is the only evidence that the yaw is
* observable.
*/
function isMatureAlignment(a, floorM = 40) {
	return isUsableAlignment(a) && a.gpsExtentM !== void 0 && Number.isFinite(a.gpsExtentM) && a.gpsExtentM >= floorM;
}
/** The pick at the moment itself: that moment's alignment, mature or not. */
function openMatureAlignmentPick(now, floorM = 40) {
	return {
		alignment: now,
		mature: isMatureAlignment(now, floorM)
	};
}
/**
* One alignment change after the moment: a mature pick stays where it is;
* an open one moves to `now` when `now` is usable (and is mature from then
* on if `now` is). An unusable `now` (no matrix or no zero: a GPS gap, a
* reset store) leaves the pick on the last usable alignment.
*/
function advanceMatureAlignmentPick(pick, now, floorM = 40) {
	if (pick.mature || !isUsableAlignment(now)) return pick;
	return openMatureAlignmentPick(now, floorM);
}
//#endregion
export { MATURE_GPS_EXTENT_M, advanceMatureAlignmentPick, checkMatureGpsExtentM, isMatureAlignment, isUsableAlignment, openMatureAlignmentPick };
